import React from 'react';
import * as yup from 'yup';
import ApolloForm from '$/components/ApolloForm';
import {
  AddressInput,
  BillingCycle,
  UpdateBillingInfoInput,
  useChargePendingInvoicesMutation,
  useCurrentBillingInfoQuery,
  useRestoreSubscriptionMutation,
  useUpdateBillingInfoMutation,
} from '$/GraphQL/schema/schema';
import { StripeCardElementChangeEvent } from '@stripe/stripe-js';
import { BillingAddressAndCC } from '$/components/BillingAddressAndCC/BillingAddressAndCC';
import { css } from '@emotion/react';
import tw from 'twin.macro';
import { useMe } from '$/hooks/useMe';
import { useCreatePaymentMethod } from '$/hooks/useCreatePaymentMethod';
import { useStripe } from '@stripe/react-stripe-js';
import Modal from '../ui/Modal';
import Creds from '../ui/Creds';
import Form from '../ui/Form';
import Input from '../ui/Input/Basics/Input';
import Button from '../ui/Button';

export const UpdateBillingInfoModal: React.FC<{
  fresh?: boolean;
  isRestoring?: boolean;
  updateBillingAddress?: boolean;
  toggleModal?: () => void;
}> = ({ toggleModal, fresh, isRestoring, updateBillingAddress }) => {
  const [updateBillingInfoMutation] = useUpdateBillingInfoMutation();
  const [restoreSubscriptionMutation] = useRestoreSubscriptionMutation();
  const [chargePendingInvoicesMutation] = useChargePendingInvoicesMutation();
  const createPaymentMethod = useCreatePaymentMethod();
  const me = useMe();
  const stripe = useStripe();
  const currentBillingInfoQuery = useCurrentBillingInfoQuery();
  const isCardCompleted = React.useRef<boolean>(false);

  const validationSchema = yup.object().shape({
    card: yup.mixed().test('card', 'This field is required.', () => {
      if (updateBillingAddress) return true;
      return isCardCompleted.current;
    }),
    firstName: yup.string().trim().required(),
    lastName: yup.string().trim().required(),
    team: yup.object().shape({
      address: yup.object().shape({
        company: yup.string().trim(),
        address1: yup.string().trim().required(),
        city: yup.string().trim().required(),
        postal: yup.string().trim().required(),
        country: yup.string().trim().required(),
        province: yup.string().trim().required(),
      }),
    }),
  });

  const chargePendingInvoices = React.useCallback(async (): Promise<void> => {
    const { data } = await chargePendingInvoicesMutation();
    if (!data?.chargePendingInvoices?.paymentIntentSecret) return;
    const confirmResult = await stripe?.confirmCardPayment(
      data.chargePendingInvoices.paymentIntentSecret
    );
    if (confirmResult?.error) throw confirmResult.error?.message;
    await chargePendingInvoices();
  }, [chargePendingInvoicesMutation, stripe]);

  const handleChangeBillingInfo = React.useCallback(
    async (user: UpdateBillingInfoInput, paymentIntentId?: string): Promise<void> => {
      const address = user.team.address! as AddressInput & { company?: string };
      address.address2 = address.address1;
      address.address1 = address.company ?? address.address1;
      delete address.company;

      const data: UpdateBillingInfoInput = { ...user };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (data as any).card;

      if (!updateBillingAddress && !paymentIntentId) {
        const paymentMethod = await createPaymentMethod({
          address: {
            line1: address.address1,
            line2: address.address2 || undefined,
            city: address.city!,
            postal_code: address.postal,
            state: address.province,
            country: address.country,
          },
          email: me.email,
          name: `${[user.firstName, user.lastName].join(' ')}`,
        });

        data.team.billingCycle = BillingCycle.Month;
        data.team.last4 = paymentMethod!.card!.last4 || '';
        data.team.ccType = paymentMethod!.card!.brand || '';
        data.team.paymentMethodID = paymentMethod!.id || '';
      }

      if (isRestoring) {
        const result = await restoreSubscriptionMutation({
          variables: {
            data,
            fresh: Boolean(fresh),
            paymentIntentId,
          },
        });
        if (result.data?.restoreSubscription?.paymentIntentSecret) {
          const confirmResult = await stripe?.confirmCardPayment(
            result.data?.restoreSubscription?.paymentIntentSecret
          );
          if (confirmResult?.error) throw confirmResult.error.message;
          if (confirmResult?.paymentIntent?.id) {
            await handleChangeBillingInfo(user, confirmResult.paymentIntent.id);
          }
        } else if (result.data?.restoreSubscription?.team.id) {
          window.location.reload();
        }
      } else {
        const updateBillingInfoMutationResult = await updateBillingInfoMutation({
          variables: {
            data,
          },
        });

        // charge pending invoices
        if (updateBillingInfoMutationResult.data?.updateBillingInfo && data.team.paymentMethodID) {
          await chargePendingInvoices();
        }
        window.location.reload();
      }
    },
    [
      createPaymentMethod,
      fresh,
      isRestoring,
      me.email,
      restoreSubscriptionMutation,
      stripe,
      updateBillingAddress,
      updateBillingInfoMutation,
      chargePendingInvoices,
    ]
  );

  const onSubmit = React.useCallback(
    async (user: UpdateBillingInfoInput): Promise<void> => {
      await handleChangeBillingInfo(user);
    },
    [handleChangeBillingInfo]
  );

  if (!currentBillingInfoQuery.data?.currentBillingInfo) {
    return null;
  }

  const billingInfo = currentBillingInfoQuery.data.currentBillingInfo;

  return (
    <ApolloForm
      onSubmit={onSubmit}
      resetAfterSave={false}
      defaultValues={{
        firstName: billingInfo.firstName,
        lastName: billingInfo.lastName,
        team: {
          address: {
            city: billingInfo.city,
            country: billingInfo.country,
            company: billingInfo.address1,
            address1: billingInfo.address2,
            postal: billingInfo.postal,
            province: billingInfo.province,
          },
        },
      }}
      validationSchema={validationSchema}
    >
      <>
        <Modal widthClass='container mx-auto max-w-55e' closeOnClickOutside onClose={toggleModal}>
          <Modal.Header gray>
            <Modal.Title>
              {updateBillingAddress ? 'Update billing address' : 'Update payment details'}
            </Modal.Title>
            {toggleModal && <Modal.Close onClick={toggleModal} />}
          </Modal.Header>
          <Modal.Body
            css={css`
              ${Creds.Subtitle} {
                ${tw`font-body`}
              }
            `}
          >
            <div
              className='flex flex-wrap'
              css={css`
                ${Form.Field} {
                  ${tw`w-1/2 px-2 mb-4`}
                }
              `}
            >
              <div className='flex-1'>
                <Creds.Subtitle className='px-2'>Your Information</Creds.Subtitle>
                <div
                  className='flex flex-wrap'
                  css={css`
                    ${Form.Field} {
                      ${tw`w-1/2 px-2 mb-4`}
                    }
                  `}
                >
                  <Form.Field
                    component={<Input autoComplete='given-name' placeholder='First name' />}
                    name='firstName'
                  />

                  <Form.Field
                    name='lastName'
                    component={<Input autoComplete='family-name' placeholder='Last name' />}
                  />
                </div>
                <Creds.Separator />
                <BillingAddressAndCC
                  update
                  hideCardInput={updateBillingAddress}
                  onCardChange={(event: StripeCardElementChangeEvent): void => {
                    isCardCompleted.current = event.complete;
                  }}
                />
                <Form.ErrorMessage />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer sticky={false}>
            <div className='flex items-center justify-between'>
              {toggleModal ? (
                <Button className='text-color-cancel' onClick={toggleModal}>
                  Cancel
                </Button>
              ) : (
                <div />
              )}
              <Form.SubmitButton className='ml-2'>
                {isRestoring ? 'Resubscribe Now' : 'Update billing info'}
              </Form.SubmitButton>
            </div>
          </Modal.Footer>
        </Modal>
      </>
    </ApolloForm>
  );
};
